import { useQuery } from '@tanstack/react-query';
import { Share2, Users } from 'lucide-react';
import { api, apiError } from '@/lib/api';
import { openTelegramLink } from '@/lib/telegram';
import { money } from '@/lib/format';
import { useI18n } from '@/i18n';
import { useAuth } from '@/context/AuthContext';
import { CopyRow, Notice, PageLoading, PageTitle } from '@/components/ui';
import { NeedAccount } from './NeedAccount';
import type { Referrals } from '@/types/api';

export function ReferralsPage() {
    const { t, te, lang } = useI18n();
    const { me } = useAuth();
    const q = useQuery({ queryKey: ['referrals'], queryFn: async () => (await api.get<Referrals>('/referrals')).data, enabled: !!me?.registered });
    if (!me?.registered) return <NeedAccount />;
    if (q.isLoading) return <PageLoading />;
    if (q.isError || !q.data) return <Notice tone="warn">{te(apiError(q.error))}</Notice>;
    const r = q.data;
    const share = () => {
        if (!r.link) return;
        const text = t('referrals.shareText', { brand: me.brand.name });
        openTelegramLink(`https://t.me/share/url?url=${encodeURIComponent(r.link)}&text=${encodeURIComponent(text)}`);
    };
    const stats = [
        { label: t('referrals.total'), value: String(r.total) },
        { label: t('referrals.completed'), value: String(r.completed) },
        { label: t('referrals.rewards'), value: money(r.rewards, r.currency, lang) }
    ];
    return (
        <div className="space-y-4">
            <PageTitle title={t('referrals.title')} subtitle={t('referrals.subtitle')} />
            {!r.active && <Notice tone="warn">{t('referrals.inactive')}</Notice>}
            <div className="card-lime space-y-2">
                <div className="flex items-center gap-2"><Users className="w-5 h-5 text-brand-300" /><span className="font-bold">{t('referrals.howTitle')}</span></div>
                <p className="text-sm text-ink-muted leading-relaxed">{t('referrals.how', { you: r.referrerPercent, friend: r.referredPercent })}</p>
            </div>
            <div className="grid grid-cols-3 gap-2">
                {stats.map(s => (
                    <div key={s.label} className="card text-center px-2 py-3">
                        <div className="text-lg font-black font-display text-lime">{s.value}</div>
                        <div className="text-[10px] text-ink-muted mt-0.5">{s.label}</div>
                    </div>
                ))}
            </div>
            <CopyRow label={t('referrals.code')} value={r.code} />
            {r.link && <CopyRow label={t('referrals.link')} value={r.link} />}
            {r.link && <button className="btn-primary w-full" onClick={share}><Share2 className="w-4 h-4" /> {t('referrals.share')}</button>}
        </div>
    );
}
